import React, {ReactNode, useMemo} from 'react';

import './styles.scss';
import {Category, categoryOptions, DataPoint} from '../../App';
import CountryFlag from '../CountryFlag/CountryFlag';
import Icon from '../Icon/Icon';
import {displayInvestment} from '../../utils';

interface LinkDetailProps {
  dataPoint: DataPoint;
}

export function isEmpty(value: string) {
  return (
    value === '-' ||
    value === '?' ||
    value === 'n/a' ||
    value === 'N/A' ||
    !value
  );
}

const LinkDetail = ({dataPoint}: LinkDetailProps) => {
  if (!dataPoint) return <div>Loading...</div>;

  const renderYesNo = (stringValue: string) => {
    if (
      !stringValue ||
      stringValue === '?' ||
      stringValue === '-' ||
      stringValue === ''
    ) {
      return <span>-</span>;
    }
    const value = String(stringValue).toLowerCase() === 'yes';
    return (
      <div className="yes-no">
        <span className={value ? 'checked' : ''}>Yes</span>
        <span className={!value ? 'checked' : ''}>No</span>
      </div>
    );
  };

  const renderYesNoPartially = (stringValue: string) => {
    if (
      !stringValue ||
      stringValue === '?' ||
      stringValue === '-' ||
      stringValue === ''
    ) {
      return <span>-</span>;
    }
    const lowercaseValue = String(stringValue).toLowerCase();
    return (
      <div className="yes-no">
        <span className={lowercaseValue === 'yes' ? 'checked' : ''}>Yes</span>
        <span className={lowercaseValue === 'partially' ? 'checked' : ''}>
          Partially
        </span>
        <span className={lowercaseValue === 'no' ? 'checked' : ''}>No</span>
      </div>
    );
  };

  const renderStatus = (status: string) => {
    if (isEmpty(status)) {
      return <span>N/A</span>;
    }

    const statusClasses = new Map<typeof status, string>([
      ['live', 'live'],
      ['future', 'future'],
    ]);

    const statusLabels = new Map<typeof status, string>([
      ['live', 'In production'],
      ['future', 'Planned'],
    ]);
    return (
      <div className={`status ${statusClasses.get(status.toLowerCase())}`}>
        <span>{statusLabels.get(status.toLowerCase()) || 'N/A'}</span>
      </div>
    );
  };

  const renderTags = (tags: string) => {
    if (isEmpty(tags)) {
      return <span>-</span>;
    }
    // Split and remove empty tags
    const tagsArray = tags
        .split('/')
        .map((tag) => tag.trim())
        .filter((tag) => Boolean);
    return (
      <>
        {tagsArray.map((tag) => {
          return (
            <div key={tag}>
              <span className="tag">{tag}</span>
            </div>
          );
        })}
      </>
    );
  };

  const renderHelper = (
      label: string,
      value: string,
      renderFcOrValue: Function | string | ReactNode,
  ) => {
    if (isEmpty(value)) return null;
    return (
      <>
        <label>{label}</label>
        <span>
          {typeof renderFcOrValue === 'function' ?
            renderFcOrValue() :
            renderFcOrValue}
        </span>
      </>
    );
  };

  return (
    <div className={`LinkDetail`}>
      <h1>
        <CountryFlag country={dataPoint.Investor_country} />
        {dataPoint.Entity}
      </h1>

      <div className="info-wrapper">
        <div>
          <label>Investment amount</label>
          <h2>{displayInvestment(dataPoint.Amount_EUR_mln)}</h2>
          <label>Location</label>
          <span>
            <p className="city-name">
              <span>
                {isEmpty(dataPoint.Location) ? '' : `${dataPoint.Location}`}
              </span>
              <span>
                <CountryFlag country={dataPoint.Country} />
                {dataPoint.Country}
              </span>
            </p>
          </span>
          <label>Category</label>
          <span className="category">
            <Icon type={dataPoint.Category} width={24} />
            {dataPoint.Category}
          </span>
          <label>Production status</label>
          {renderStatus(dataPoint.Production_status.toLowerCase())}
        </div>
        <div>
          <label>Employment</label>
          <span className="tag">{dataPoint.Employment || 'N/A'}</span>
          <label>Start of production</label>
          <span className="tag">{dataPoint['Start of production'] || 'N/A'}</span>
          <label>Capacity</label>
          <span className="tag">{dataPoint.Capacity || 'N/A'}</span>
          {dataPoint.Category === Category.EV_Manufacturing && (
            <>
              <label>EV model and type</label>
              <span className="tag">{dataPoint['EV model and type'] || 'N/A'}</span>
            </>
          )}
          {dataPoint.Category !== Category.EV_Manufacturing && (
            <>
              <label>Product</label>
              <span className="tag">{dataPoint.Product || 'N/A'}</span>
            </>
          )}
          {dataPoint.Note && (
            <>
              <label>Note</label>
              <p>{dataPoint.Note || 'N/A'}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkDetail;
